import { ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update'
import * as Sentry from '@sentry/vue'

const PREFS_KEY_VERSION = 'appUpdate_lastPromptedVersion'
const PREFS_KEY_AT = 'appUpdate_lastPromptedAt'
const RE_PROMPT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

/**
 * Manages the iOS in-app update prompt.
 *
 * Design decisions (DEVAMS-2164):
 * - iOS only: Capacitor AppUpdate plugin's getAppUpdateInfo() only returns useful
 *   data on iOS (and Android); on web it throws. Guard is applied at the top of
 *   checkForUpdate() to keep it a no-op on web.
 * - Singleton pattern: module-level reactive state ensures App.vue and
 *   AppUpdateDialog.vue share the same showUpdateDialog/availableVersion refs
 *   without needing provide/inject or prop drilling.
 * - Soft prompt: we never force-update. The dialog is dismissable. We re-prompt
 *   once per new available version OR after 7 days on the same version to keep
 *   users aware without being intrusive.
 * - No backend calls: version detection is entirely via Apple's iTunes Lookup API
 *   through the plugin. Zero backend involvement.
 * - Persistence via @capacitor/preferences: survives app restarts.
 * - Errors are silently caught (and sent to Sentry) — a version-check failure
 *   must never crash the app.
 */

// Module-level singleton state — shared across all callers of this composable
const showUpdateDialog = ref(false)
const availableVersion = ref('')

async function checkForUpdate(): Promise<void> {
  if (Capacitor.getPlatform() !== 'ios') return

  try {
    const info = await AppUpdate.getAppUpdateInfo()
    const available = info.availableVersionName ?? ''

    if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) return

    // Check persistence to avoid over-prompting
    const [storedVersion, storedAt] = await Promise.all([
      Preferences.get({ key: PREFS_KEY_VERSION }),
      Preferences.get({ key: PREFS_KEY_AT }),
    ])

    const lastPromptedVersion = storedVersion.value ?? ''
    const lastPromptedAt = storedAt.value ? Number(storedAt.value) : 0
    const now = Date.now()

    const isNewVersion = lastPromptedVersion !== available
    const isStale = now - lastPromptedAt > RE_PROMPT_INTERVAL_MS

    if (isNewVersion || isStale) {
      availableVersion.value = available
      showUpdateDialog.value = true
    }
  } catch (err) {
    // Silently absorb errors on web/Android/test environments
    Sentry.captureException(err, { tags: { feature: 'app-update', platform: Capacitor.getPlatform() } })
  }
}

async function dismiss(): Promise<void> {
  showUpdateDialog.value = false
  try {
    await Promise.all([
      Preferences.set({ key: PREFS_KEY_VERSION, value: availableVersion.value }),
      Preferences.set({ key: PREFS_KEY_AT, value: Date.now().toString() }),
    ])
  } catch (err) {
    Sentry.captureException(err, { tags: { feature: 'app-update', action: 'dismiss' } })
  }
}

async function openAppStore(): Promise<void> {
  try {
    await AppUpdate.openAppStore({ appId: '6443798955' })
  } catch (err) {
    Sentry.captureException(err, { tags: { feature: 'app-update', action: 'open-store' } })
  }
  // Always dismiss so we record the prompt date regardless of whether the user updates
  await dismiss()
}

export function useAppUpdate() {
  return {
    showUpdateDialog,
    availableVersion,
    checkForUpdate,
    dismiss,
    openAppStore,
  }
}
